/**
 * The search filters, as they travel between the URL and the API.
 *
 * Filters live in `searchParams` and nowhere else. That makes a filtered
 * search a link — shareable, bookmarkable, survivable across a refresh and
 * a back button — and it means the server component can render the results
 * directly rather than waiting for a client effect to tell it what to ask
 * for.
 *
 * Deliberately not a `'use client'` module: both the RSC page and the
 * filter island import it, and `scripts/client-boundary-check.mjs` fails
 * the build if a server module reaches into a client one.
 */
import type {CoverageType, Level, OpportunityKind, Region} from '../api/types'

/**
 * Which part of the page is showing.
 *
 * `match` is "For you": the catalogue scored against the student's profile
 * (`GET /opportunities/match`).
 */
export type SearchMode = 'match' | 'browse' | 'universities'

const MODES: SearchMode[] = ['match', 'browse', 'universities']

/**
 * The Scholarships tab is hidden for now: nothing keeps its catalogue
 * current. Everything behind it — the route, the API, saved searches —
 * stays, so bringing it back is setting this to `true`.
 */
export const SCHOLARSHIPS_TAB_ENABLED: boolean = false

/** Whether this tab is offered at the moment. */
export function modeEnabled(mode: SearchMode): boolean {
  return mode !== 'browse' || SCHOLARSHIPS_TAB_ENABLED
}

/** The tab a search opens on when the URL names none, or names a hidden one. */
export const DEFAULT_MODE: SearchMode = SCHOLARSHIPS_TAB_ENABLED ? 'browse' : 'universities'

export interface SearchFilters {
  q: string
  kind: OpportunityKind | null
  countries: string[]
  level: Level | null
  coverageType: CoverageType | null
  major: string | null
  deadlineBefore: string | null
  maxTuitionUsdYear: number | null
  maxQsRank: number | null
  /** The QS classification — universities only. */
  region: Region | null
  /** How the list is ordered (SRCH-13); `null` is the endpoint's default. */
  sort: SortKey | null
}

/** Every sort either endpoint knows. Which apply to a tab: `SORTS_BY_MODE`. */
export type SortKey = 'relevance' | 'deadline' | 'tuition' | 'name' | 'qs_rank'

/**
 * The sorts each tab offers, default first.
 *
 * "For you" has none: its order is the matching engine's grouping. The
 * first entry is what the API does without a `sort`, and `relevance` only
 * means something when there is a name to match.
 */
export const SORTS_BY_MODE: Record<SearchMode, readonly SortKey[]> = {
  match: [],
  browse: ['deadline', 'relevance', 'tuition', 'name'],
  // No `tuition`: the QS table carries no fees, so there is nothing to order by.
  universities: ['qs_rank', 'relevance', 'name']
}

/** The sorts worth offering for these filters in this tab. */
export function sortsFor(mode: SearchMode, filters: SearchFilters): SortKey[] {
  return SORTS_BY_MODE[mode].filter(
    (key) => key !== 'relevance' || (appliesTo(mode, 'q') && filters.q !== '')
  )
}

/**
 * The most rows one search shows (SRCH-13). "Show more" re-requests every
 * row shown so far, and the API caps `limit` at the same number.
 */
export const MAX_SHOWN = 200

export const EMPTY_FILTERS: SearchFilters = {
  q: '',
  kind: null,
  countries: [],
  level: null,
  coverageType: null,
  major: null,
  deadlineBefore: null,
  maxTuitionUsdYear: null,
  maxQsRank: null,
  region: null,
  sort: null
}

export const PAGE_SIZE = 20

/**
 * How many results a signed-out visitor sees. Enough to judge the catalogue
 * by; the rest, with saving and comparing, is what signing up is for.
 */
export const GUEST_LIMIT = 10

const KINDS: OpportunityKind[] = ['scholarship', 'program']
const LEVELS: Level[] = ['bachelor', 'master']
export const REGIONS: Region[] = [
  'africa',
  'asia',
  'europe',
  'north_america',
  'south_america',
  'oceania'
]
const SORT_KEYS: SortKey[] = ['relevance', 'deadline', 'tuition', 'name', 'qs_rank']
const COVERAGE_TYPES: CoverageType[] = [
  'full_funding',
  'tuition_free',
  'tuition_only',
  'partial',
  'stipend_only'
]

/**
 * Which filters mean anything in each mode, and the single place that
 * decides it.
 *
 * A university has no funding type and no deadline. Without one list,
 * "which control do I render?", "which do I count?" and "which do I send?"
 * drift apart — and the visible symptom is a "Filters (1)" badge for a
 * control that is not on screen.
 */
export const FILTERS_BY_MODE: Record<SearchMode, readonly (keyof SearchFilters)[]> = {
  // Only the universities tab has a name search; on the others a `q` left
  // in the URL is read but not sent.
  // No level here: matching is about the profile, and the profile already
  // says which level the student is applying for.
  match: ['kind', 'countries', 'coverageType', 'major', 'deadlineBefore', 'maxTuitionUsdYear'],
  browse: [
    'kind',
    'countries',
    'level',
    'coverageType',
    'major',
    'deadlineBefore',
    'maxTuitionUsdYear',
    'sort'
  ],
  // What the QS tables can answer, and the name. The field comes from the subject tables:
  // who QS ranks in it. The level finds the rows that say which they teach
  // or have a programme at it. No budget: only the US rows carry a fee, so
  // that filter would turn most searches into empty ones.
  universities: [
    'q',
    'countries',
    'level',
    'major',
    'region',
    'maxQsRank',
    'sort'
  ]
}

/**
 * Whether a search on this tab needs a country before it shows anything.
 *
 * The scholarship tabs do. The university catalogue is fifteen hundred rows
 * that can be searched by name alone, so there a country is one more filter.
 */
export function requiresCountry(mode: SearchMode): boolean {
  return mode !== 'universities'
}

/** Whether this filter is one the given mode renders and sends. */
export function appliesTo(mode: SearchMode, key: keyof SearchFilters): boolean {
  return FILTERS_BY_MODE[mode].includes(key)
}

/** What Next hands a page as `searchParams`. */
export type RawSearchParams = Record<string, string | string[] | undefined>

function one(value: string | string[] | undefined): string | null {
  const first = Array.isArray(value) ? value[0] : value
  const trimmed = first?.trim()
  return trimmed ? trimmed : null
}

function many(value: string | string[] | undefined): string[] {
  const values = Array.isArray(value) ? value : value ? [value] : []
  return values
    .flatMap((entry) => entry.split(','))
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function oneOf<T extends string>(value: string | null, allowed: T[]): T | null {
  return value !== null && (allowed as string[]).includes(value) ? (value as T) : null
}

/**
 * Read filters out of the URL.
 *
 * Anything unrecognised is dropped rather than passed through. A student
 * who hand-edits the query string, or follows a link from an older version
 * of the page, gets a working search rather than a 422 from the API.
 */
export function parseFilters(params: RawSearchParams): SearchFilters {
  return {
    q: one(params.q)?.slice(0, 100) ?? '',
    kind: oneOf(one(params.kind), KINDS),
    countries: [
      ...new Set(
        many(params.country)
          .map((code) => code.toUpperCase())
          .filter((code) => /^[A-Z]{2}$/.test(code))
      )
    ],
    level: oneOf(one(params.level), LEVELS),
    coverageType: oneOf(one(params.coverage), COVERAGE_TYPES),
    major: one(params.major),
    deadlineBefore: isIsoDate(one(params.deadline_before)) ? one(params.deadline_before) : null,
    maxTuitionUsdYear: positiveInt(one(params.max_tuition)),
    maxQsRank: positiveInt(one(params.max_qs)),
    region: oneOf(one(params.region), REGIONS),
    sort: oneOf(one(params.sort), SORT_KEYS)
  }
}

function positiveInt(raw: string | null): number | null {
  if (raw === null) return null
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

export function parseMode(params: RawSearchParams): SearchMode {
  const mode = oneOf(one(params.mode), MODES)
  return mode !== null && modeEnabled(mode) ? mode : DEFAULT_MODE
}

export function parseOffset(params: RawSearchParams): number {
  const raw = one(params.offset)
  const parsed = raw === null ? 0 : Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, 10_000) : 0
}

/** Back to a query string, for `router.push` and for `<a href>`. */
export function toSearchParams(
  filters: SearchFilters,
  {mode, offset = 0}: {mode: SearchMode; offset?: number}
): URLSearchParams {
  const params = new URLSearchParams()
  // Written out even for the default mode: a tab link that says which tab
  // it is stays readable in a shared URL, and the tab strip can mark itself
  // current from the query string alone.
  params.set('mode', mode)
  if (filters.q) params.set('q', filters.q)
  if (filters.kind) params.set('kind', filters.kind)
  for (const code of filters.countries) params.append('country', code)
  if (filters.level) params.set('level', filters.level)
  if (filters.coverageType) params.set('coverage', filters.coverageType)
  if (filters.major) params.set('major', filters.major)
  if (filters.deadlineBefore) params.set('deadline_before', filters.deadlineBefore)
  if (filters.maxTuitionUsdYear !== null) {
    params.set('max_tuition', String(filters.maxTuitionUsdYear))
  }
  if (filters.maxQsRank !== null) params.set('max_qs', String(filters.maxQsRank))
  if (filters.region) params.set('region', filters.region)
  if (filters.sort) params.set('sort', filters.sort)
  if (offset > 0) params.set('offset', String(offset))
  return params
}

/**
 * The API query string for these filters.
 *
 * Not the same as the URL's: the API names some parameters differently, and
 * `mode` is a page concern that picks the endpoint rather than a filter the
 * API is told about.
 */
export function toApiQuery(
  filters: SearchFilters,
  {mode, offset = 0, locale}: {mode: SearchMode; offset?: number; locale?: string}
): string {
  const params = new URLSearchParams()
  const applies = (key: keyof SearchFilters) => appliesTo(mode, key)

  // Switching tabs keeps whatever was already in the URL, so a filter that
  // does not apply here is left there — and simply not sent. Sending it
  // would 422 the endpoint that has never heard of it.
  if (applies('kind') && filters.kind) params.set('kind', filters.kind)
  if (applies('countries')) {
    for (const code of filters.countries) params.append('country', code)
  }
  if (applies('coverageType') && filters.coverageType) {
    params.set('coverage_type', filters.coverageType)
  }
  if (applies('major') && filters.major) params.set('major', filters.major)
  if (applies('deadlineBefore') && filters.deadlineBefore) {
    params.set('deadline_before', filters.deadlineBefore)
  }
  if (applies('maxTuitionUsdYear') && filters.maxTuitionUsdYear !== null) {
    params.set('max_tuition_usd_year', String(filters.maxTuitionUsdYear))
  }
  if (applies('maxQsRank') && filters.maxQsRank !== null) {
    params.set('max_qs_rank', String(filters.maxQsRank))
  }
  if (applies('region') && filters.region) params.set('region', filters.region)
  if (applies('level') && filters.level) params.set('level', filters.level)
  if (applies('q') && filters.q) params.set('q', filters.q)
  // A sort this tab does not offer — left in the URL by the other tab, or
  // `relevance` with the name search cleared — falls back to the default.
  if (filters.sort && sortsFor(mode, filters).includes(filters.sort)) {
    params.set('sort', filters.sort)
  }
  if (locale) params.set('locale', locale)

  // The matched view is capped per group rather than paged. The others ask
  // for every row shown so far, from the first (SRCH-13). It re-fetches rows the student has already seen; the
  // API caps the total at `MAX_SHOWN`, and `search_performed.limit` says
  // whether anyone goes deep enough for that to matter.
  if (mode !== 'match') {
    params.set('limit', String(shownThrough(offset)))
  }

  const query = params.toString()
  return query ? `?${query}` : ''
}

/** How many rows a search shows once "Show more" has reached `offset`. */
export function shownThrough(offset: number): number {
  return Math.min(offset + PAGE_SIZE, MAX_SHOWN)
}

/** How many of *this mode's* filters are set, for the "Filters (3)" button. */
export function countActive(filters: SearchFilters, mode: SearchMode): number {
  const isSet: Record<keyof SearchFilters, boolean> = {
    q: filters.q !== '',
    kind: filters.kind !== null,
    // Not counted where one is required: there it is the search itself
    // rather than an extra filter somebody can clear.
    countries: !requiresCountry(mode) && filters.countries.length > 0,
    level: filters.level !== null,
    coverageType: filters.coverageType !== null,
    major: filters.major !== null,
    deadlineBefore: filters.deadlineBefore !== null,
    maxTuitionUsdYear: filters.maxTuitionUsdYear !== null,
    maxQsRank: filters.maxQsRank !== null,
    region: filters.region !== null,
    // An order, not a narrowing: it never hides a result.
    sort: false
  }

  // Only what this mode actually shows. A `kind` left in the URL from the
  // browse tab must not put a "(1)" on a button whose panel has no funding
  // control on it.
  return FILTERS_BY_MODE[mode].filter((key) => isSet[key]).length
}

function isIsoDate(value: string | null): boolean {
  if (value === null) return false
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime())
}

/**
 * The profile's answers, as filters for a search that arrived without any.
 *
 * Only what one filter control can say faithfully: a single-select
 * department cannot hold three majors, so a profile with several leaves it
 * unset rather than picking one on the student's behalf.
 */
export function filtersFromProfile(
  filters: SearchFilters,
  profile: {level: Level | null; majors: string[]; budget_usd_year: number | null},
  countries: string[]
): SearchFilters {
  return {
    ...filters,
    countries,
    level: filters.level ?? profile.level,
    major: filters.major ?? (profile.majors.length === 1 ? profile.majors[0] : null),
    maxTuitionUsdYear:
      filters.maxTuitionUsdYear ??
      (profile.budget_usd_year === null ? null : Math.round(profile.budget_usd_year))
  }
}

/**
 * The search a detail page was opened from, as a safe link back to it.
 *
 * `from` arrives in the detail page's URL, so it is user input: it is parsed
 * through `parseFilters` and written out again rather than pasted into an
 * `href`, which means it can only ever produce a search link on this site —
 * never a redirect somewhere else, and never a filter the search would 422 on.
 */
export function backToSearchHref(
  locale: string,
  from: string | string[] | undefined,
  fallbackMode: SearchMode
): string {
  const query = Array.isArray(from) ? from[0] : from
  if (!query) return `/${locale}/search?mode=${fallbackMode}`

  const params = new URLSearchParams(query)
  const raw: RawSearchParams = {}
  for (const key of new Set(params.keys())) {
    const values = params.getAll(key)
    raw[key] = values.length === 1 ? values[0] : values
  }
  const mode = raw.mode === undefined ? fallbackMode : parseMode(raw)
  const back = toSearchParams(parseFilters(raw), {mode, offset: parseOffset(raw)})
  return `/${locale}/search?${back}`
}

/** A detail page's path, carrying the search it was opened from. */
export function withFrom(path: string, from: string | undefined): string {
  return from ? `${path}?${new URLSearchParams({from})}` : path
}

